import React, { Component } from 'react';
import { connect } from 'react-redux';
import { CheckCircleTwoTone, CloseCircleTwoTone, DeleteOutlined, EditOutlined, EyeOutlined, FileOutlined } from '@ant-design/icons';
import { DatePicker, Divider, InputNumber, Modal, Select, Space, Tooltip } from 'antd';
import moment from 'moment';
import qs from 'qs';
import PageTitle from '@/components/PageTitle';
import TableActionBar from '@/components/TableActionBar';
import DynamicTable from '@/components/DynamicTable';
import CreatedAtCell from '@/components/CreatedAtCell';
import { checkPermission } from '@/utils/permission';
import { cleanObject, isValidDate } from '@/utils/helpers';
import { getDocuments, getOneDocument, removeDocuments } from '@/redux/actions/documentActions';
import { getDocumentCategoriesActive } from '@/redux/actions/documentCategorieActions';
import DocumentForm from './DocumentForm';
import DocumentDetail from './DocumentDetail';

const { RangePicker } = DatePicker;
const routes = [
    { path: '/', breadcrumbName: 'Trang chủ' },
    { path: '/documents', breadcrumbName: 'Văn bản' }
];

class DocumentList extends Component {
    state = {
        isLoading: false,
        selectedRowKeys: [],
        visible: false,
        detailVisible: false,
        currentItem: null
    };

    componentDidMount() {
        this.props.getDocumentCategoriesActive();
        this.loadData(this.getQuery());
    }

    componentDidUpdate(prevProps) {
        if (prevProps.location.search !== this.props.location.search) {
            this.loadData(this.getQuery());
        }
    }

    getQuery = () => qs.parse(this.props.location.search, { ignoreQueryPrefix: true });

    loadData = query => {
        this.setState({ isLoading: true });
        return this.props.getDocuments(query).finally(() => this.setState({ isLoading: false }));
    };

    pushQuery = query => this.props.history.push({
        pathname: this.props.location.pathname,
        search: qs.stringify(cleanObject({ ...query, page: 1 }))
    });

    changeFilter = (name, value) => this.pushQuery({ ...this.getQuery(), [name]: value });

    changeDateRange = dates => {
        const query = { ...this.getQuery() };
        if (dates?.length === 2) {
            query.start_date = dates[0].format('YYYY-MM-DD');
            query.end_date = dates[1].format('YYYY-MM-DD');
        } else {
            delete query.start_date;
            delete query.end_date;
        }
        this.pushQuery(query);
    };

    edit = id => {
        this.props.getOneDocument(id).then(currentItem => this.setState({ visible: true, currentItem }));
    };

    view = id => {
        this.props.getOneDocument(id).then(currentItem => this.setState({ detailVisible: true, currentItem }));
    };

    remove = ids => this.props.removeDocuments(ids).then(() => {
        this.setState({ selectedRowKeys: [] });
        this.loadData(this.getQuery());
    });

    confirmRemove = ids => Modal.confirm({
        title: 'Xóa văn bản',
        content: 'Dữ liệu sẽ được ẩn khỏi hệ thống; tệp vật lý vẫn được giữ để bảo toàn lịch sử. Bạn muốn tiếp tục?',
        okText: 'Xóa',
        okType: 'danger',
        cancelText: 'Hủy',
        onOk: () => this.remove(ids)
    });

    renderFiles = files => {
        if (!files?.length) return '—';
        return (
            <Space direction="vertical" size={2}>
                {files.map(file => (
                    <a key={file.id || file.data} href={file.data} target="_blank" rel="noopener noreferrer">
                        <FileOutlined /> {file.name}
                    </a>
                ))}
            </Space>
        );
    };

    render() {
        const query = this.getQuery();
        const { documents, pagination, categories } = this.props;
        const rangeValue = query.start_date && query.end_date
            ? [moment(query.start_date, 'YYYY-MM-DD'), moment(query.end_date, 'YYYY-MM-DD')]
            : undefined;
        const columns = [
            { title: 'ID', dataIndex: 'id', key: 'id', sorter: true, width: 70 },
            {
                title: 'Thao tác', key: 'actions', width: 120, align: 'center', render: (_, record) => (
                    <>
                        <EyeOutlined onClick={() => this.view(record.id)} />
                        {checkPermission('documents', 'update') && <Divider type="vertical" />}
                        {checkPermission('documents', 'update') && (
                            <EditOutlined className="color-primary" onClick={() => this.edit(record.id)} />
                        )}
                        {checkPermission('documents', 'update') && checkPermission('documents', 'remove') && <Divider type="vertical" />}
                        {checkPermission('documents', 'remove') && (
                            <DeleteOutlined style={{ color: 'red' }} onClick={() => this.confirmRemove([record.id])} />
                        )}
                    </>
                )
            },
            { title: 'Tên văn bản', dataIndex: 'name', key: 'name', sorter: true, width: 280 },
            { title: 'Số/ký hiệu', dataIndex: 'document_number', key: 'document_number', sorter: true, width: 160, render: value => value || '—' },
            { title: 'Danh mục', dataIndex: ['category', 'name'], key: 'category', width: 180, render: (_, record) => record.category?.name || '—' },
            { title: 'STT', dataIndex: 'serial', key: 'serial', sorter: true, width: 90, render: value => value || '—' },
            { title: 'Năm', dataIndex: 'year', key: 'year', sorter: true, width: 90, render: value => value || '—' },
            { title: 'Ngày ban hành', dataIndex: 'issue_date', key: 'issue_date', sorter: true, width: 150, render: value => value ? isValidDate(value, false) : '—' },
            { title: 'Ngày hết hạn', dataIndex: 'expiry_date', key: 'expiry_date', sorter: true, width: 130, render: value => value ? isValidDate(value, false) : '—' },
            { title: 'Văn bản gốc', dataIndex: 'original_documents', key: 'original_documents', width: 220, render: this.renderFiles },
            { title: 'Văn bản đã ký', dataIndex: 'signed_documents', key: 'signed_documents', width: 220, render: this.renderFiles },
            {
                title: 'Ngày tạo', dataIndex: 'created_at', key: 'created_at', sorter: true, width: 220,
                render: (value, record) => <CreatedAtCell createdAt={value} user={record.created_user} />
            },
            {
                title: 'Trạng thái', dataIndex: 'status', key: 'status', align: 'center', width: 110,
                render: value => Number(value) === 1
                    ? <Tooltip title="Kích hoạt"><CheckCircleTwoTone twoToneColor="#52c41a" /></Tooltip>
                    : <Tooltip title="Ngừng sử dụng"><CloseCircleTwoTone twoToneColor="#e74c3c" /></Tooltip>
            }
        ];

        return (
            <div>
                <PageTitle routes={routes} title="Quản lý văn bản" />
                <TableActionBar
                    isShowAddButton={checkPermission('documents', 'create')}
                    isShowDeleteButton={checkPermission('documents', 'remove')}
                    disabled={!this.state.selectedRowKeys.length}
                    onAdd={() => this.setState({ visible: true, currentItem: null })}
                    onDelete={() => this.confirmRemove(this.state.selectedRowKeys)}
                    onSearch={value => this.changeFilter('keyword', value)}
                    defaultKeyword={query.keyword}
                    activeFilter={Boolean(query.categories_id || query.status !== undefined || query.year || query.start_date)}
                    filterColSpan={6}
                    filters={[
                        <Select
                            key="category"
                            allowClear
                            showSearch
                            optionFilterProp="children"
                            placeholder="Danh mục"
                            style={{ width: '100%' }}
                            value={query.categories_id ? Number(query.categories_id) : undefined}
                            onChange={value => this.changeFilter('categories_id', value)}
                        >
                            {categories.map(item => <Select.Option key={item.id} value={item.id}>{item.name}</Select.Option>)}
                        </Select>,
                        <Select
                            key="status"
                            allowClear
                            placeholder="Trạng thái"
                            style={{ width: '100%' }}
                            value={query.status !== undefined ? Number(query.status) : undefined}
                            onChange={value => this.changeFilter('status', value)}
                        >
                            <Select.Option value={1}>Kích hoạt</Select.Option>
                            <Select.Option value={0}>Ngừng sử dụng</Select.Option>
                        </Select>,
                        <InputNumber
                            key="year"
                            min={1901}
                            max={2155}
                            placeholder="Năm"
                            style={{ width: '100%' }}
                            value={query.year ? Number(query.year) : undefined}
                            onChange={value => this.changeFilter('year', value)}
                        />,
                        <RangePicker
                            key="created-range"
                            format="DD/MM/YYYY"
                            style={{ width: '100%' }}
                            value={rangeValue}
                            onChange={this.changeDateRange}
                        />
                    ]}
                >
                    {this.state.selectedRowKeys.length > 0 && <span>Đang chọn {this.state.selectedRowKeys.length} bản ghi</span>}
                </TableActionBar>
                <div className="has-custom-column-table-btn">
                    <DynamicTable
                        moduleKey="document_list"
                        columns={columns}
                        dataSource={documents}
                        isLoading={this.state.isLoading}
                        pagination={pagination}
                        rowSelection={{
                            selectedRowKeys: this.state.selectedRowKeys,
                            onChange: selectedRowKeys => this.setState({ selectedRowKeys })
                        }}
                    />
                </div>
                <DocumentForm
                    visible={this.state.visible}
                    currentItem={this.state.currentItem}
                    onCancel={() => this.setState({ visible: false, currentItem: null })}
                    reloadData={() => this.loadData(this.getQuery())}
                />
                <DocumentDetail
                    visible={this.state.detailVisible}
                    currentData={this.state.currentItem}
                    onCancel={() => this.setState({ detailVisible: false, currentItem: null })}
                    onEdit={id => this.setState({ detailVisible: false }, () => this.edit(id))}
                />
            </div>
        );
    }
}

const mapStateToProps = state => ({
    documents: state.documentReducer.documents,
    pagination: state.documentReducer.pagination,
    categories: state.documentCategorieReducer.document_categories_active
});

const mapDispatchToProps = dispatch => ({
    getDocuments: filter => dispatch(getDocuments(filter)),
    getOneDocument: id => dispatch(getOneDocument(id)),
    removeDocuments: ids => dispatch(removeDocuments(ids)),
    getDocumentCategoriesActive: () => dispatch(getDocumentCategoriesActive())
});

export default connect(mapStateToProps, mapDispatchToProps)(DocumentList);
